import { FC, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router";
import { useTranslation } from "react-i18next";
import { DateTime } from "luxon";

import { useAlert } from "capitalrx-components-2.0/lib/general";
import { Shelf, Stack, useResponsive } from "capitalrx-components-2.0/lib/layout";
import { Body, Semibold, Subtitle } from "capitalrx-components-2.0/lib/typography";

import { getPharmacies } from "@/api/axios/pharmacies";
import { ListPharmacyPayload, PharmacyInfo } from "@/api/types";
import { useNavigateSearch, useSelectedEnrollmentPlan } from "@/premium/hooks";
import { PrintableWrapper, ThemedButton } from "@/premium/themedComponents";
import { EnrollmentContext } from "@/state";
import "./PrintPharmaciesPage.scss";

export const PrintPharmaciesPage: FC = () => {
  const { t: translate } = useTranslation();
  const [state] = useContext(EnrollmentContext);
  const { enrollmentPlanCsid } = useSelectedEnrollmentPlan();
  const navigate = useNavigateSearch();
  const alert = useAlert();
  const { logo } = useResponsive(
    useMemo(() => {
      return {
        logo: {
          mobile: state.experienceData?.theme?.logo_mobile ?? state.experienceData?.theme?.logo_desktop,
          desktop: state.experienceData?.theme?.logo_desktop,
        },
      };
    }, [state.experienceData.theme]),
  );

  const [pharmacies, setPharmacies] = useState<PharmacyInfo[]>([]);
  const [searchParams] = useSearchParams();

  const latitude = searchParams.get("latitude");
  const longitude = searchParams.get("longitude");
  const zipCode = searchParams.get("zipCode");
  const pharmacyName = searchParams.get("pharmacyName");

  const onPrint = useCallback(() => {
    window.print();
  }, []);

  useEffect(() => {
    const loadPharmacies = async () => {
      if (enrollmentPlanCsid) {
        let pharmacyListBody: ListPharmacyPayload = {
          results_per_page: 30,
          page_number: 1,
          latitude: latitude,
          longitude: longitude,
          enrollment_plan_id: enrollmentPlanCsid.id,
          skip_map_bounds_check: true,
        };

        if(pharmacyName) {
          pharmacyListBody = { ...pharmacyListBody, pharmacy_name: pharmacyName };
        }

        const results = await getPharmacies(pharmacyListBody);
        setPharmacies(results.results.map((p) => p.pharmacy));
      }
    };

    loadPharmacies().catch((err) => {
      alert({
        type: "error",
        style: "toast",
        message: translate("PRINT_PHARMACIES.unableToLoadPharmacies"),
      });
      navigate("/");
    });
  }, [latitude, longitude, enrollmentPlanCsid, pharmacyName]);

  const now = useMemo(() => {
    return DateTime.now().toLocaleString({
      month: "long",
      day: "numeric",
      year: "numeric",
    });
  }, []);

  return (
    <PrintableWrapper className="printPharmaciesWrapper">
      <Stack>
        <Shelf>
          <img src={logo} style={{ maxHeight: 64 }} alt="client-logo" />
          <Shelf.Spacer />
          <Stack>
            <ThemedButton className="printButton" appearance="text" icon="print" onClick={onPrint}></ThemedButton>
            <Stack.Spacer />
          </Stack>
        </Shelf>

        <Shelf>
          <Subtitle>{translate("PRINT_PHARMACIES.findPharmacySearchResults")}</Subtitle>
          <Shelf.Spacer />
          <Body>{translate("PRINT_PHARMACIES.prepared")} {now}</Body>
        </Shelf>

        <Stack spacing="none">
          <Body>
            {translate("PRINT_PHARMACIES.resultsThatMatchYourZipCode")} <Semibold>{zipCode}</Semibold>
          </Body>
          <Body>{translate("PRINT_PHARMACIES.theseResultsOnlyHave")}</Body>
        </Stack>

        <table className="pharmaciesTable">
          <thead>
            <tr>
              <th>
                <Semibold>{translate("PRINT_PHARMACIES.pharmacyName")}</Semibold>
              </th>
              <th>
                <Semibold>{translate("PRINT_PHARMACIES.address")}</Semibold>
              </th>
              <th>
                <Semibold>{translate("PRINT_PHARMACIES.phone")}</Semibold>
              </th>
              <th>
                <Semibold>{translate("PRINT_PHARMACIES.distanceFromLocation")}</Semibold>
              </th>
            </tr>
          </thead>
          <tbody>
            {pharmacies.map((pharm) => {
              return (
                <tr key={pharm.npi} className="pharmacyRow">
                  <td>
                    <Semibold>{pharm.name}</Semibold>
                    {pharm.pharmacy_display_rules?.display_tag && <Body>{pharm.pharmacy_display_rules.display_tag}</Body>}
                  </td>

                  <td>
                    <Body>{pharm.pharmacy_display_rules?.description && <>{pharm.pharmacy_display_rules.description}</>}</Body>
                    <Body>{!pharm.pharmacy_display_rules?.description && pharm.address_line_1 && <>{pharm.address_line_1}</>}</Body>
                    <Body>{!pharm.pharmacy_display_rules?.description && pharm.address_line_2 && <>{pharm.address_line_2}</>}</Body>
                    <Body>
                      {!pharm.pharmacy_display_rules?.description && pharm.city && pharm.state && pharm.zip && (
                        <>
                          {pharm.city}, {pharm.state} {pharm.zip}
                        </>
                      )}
                    </Body>
                  </td>

                  <td>
                    <Body>{pharm.phone}</Body>
                  </td>

                  <td>
                    <Body>
                      {pharm.pharmacy_display_rules?.display_on_map !== "never" && (
                        <>{(Math.round(Number(pharm.distance) * 100) / 100).toFixed(2)} miles</>
                      )}
                    </Body>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </Stack>
    </PrintableWrapper>
  );
};
